/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useJobQaTabController.ts
 * FILE_ID: JOBHUB-QA-TAB-CONTROLLER-001
 * PURPOSE: Compose the creation and execution hooks consumed by the QA frame.
 */
import { useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useIsMdUp, type UndoEntry } from '@quotech/ui'
import { useJobHubDataAdapter } from '../../jobHubDataAdapter'
import type { QuoteRow } from '../../jobDetailTypes'
import { createQaApi, type QaAreaExecution } from '../../qa/qaApi'
import { useQaCapabilities } from '../../qa/useQaCapabilities'
import type { JobClassificationContext } from '../../qa/classification'
import { normalizeList } from '../../qa/qaApiHelpers'
import { wrapQaApiWithUndo } from './qaApiUndo'
import { areaOfCheck, areaOfChecklist, childrenOfArea, childrenOfChecklist } from './qaSelectionFollowArea'
import { useQaAssigneeRoster } from './useQaAssigneeRoster'
import { useQaCreation } from './useQaCreation'
import { useQaHistory } from './useQaHistory'
import { useQaItpExecutions } from './useQaItpExecutions'
import { useQaTreeMoves } from './useQaTreeMoves'
import { useQaSelection, type QaSelectionScope } from './useQaSelection'
import { useQaJobQuotes } from './useQaJobQuotes'
import { useQaWorkspace } from './useQaWorkspace'
import type { QaTemplateWriteChoice } from './templates/qaTemplateWriteTarget'

/**
 * The other Job Hub sections, as the phone QA tab has to offer them: on a
 * phone the QA tab hides the tab strip to give the inspection the screen, so
 * the strip's destinations move into the QA More sheet instead of vanishing.
 */
export interface QaPhoneNav {
  tabs: { key: string; label: string }[]
  onTab: (key: string) => void
}

export interface JobQaTabProps {
  jobId: number
  orgId?: number
  quotes: QuoteRow[]
  quotesLoading?: boolean
  onOpenQuote?: (quote: QuoteRow) => void
  focusInspectionId?: string | null
  jobContext?: JobClassificationContext
  /** Phone-only: replaces the hidden tab strip. Omit and the group is absent. */
  phoneNav?: QaPhoneNav
  /** QA hub record page only. Omitted means the jobs hub collapse stays. */
  openSetup?: boolean
}

export function shouldFocusQaCheck(
  activeAreaId: string | null,
  activeChecklistId: string | null,
  areaId: string | null | undefined,
  checklistId?: string | null,
): boolean {
  if (!areaId) return false
  const sameArea = areaId === activeAreaId
  const sameList = checklistId == null || checklistId === activeChecklistId
  return !(sameArea && sameList)
}

export function useJobQaTabController(props: JobQaTabProps) {
  const { client } = useJobHubDataAdapter()
  const queryClient = useQueryClient()
  const registerRef = useRef<(entry: UndoEntry) => void>(() => {})
  const activeAreaRef = useRef<string | null>(null)
  const api = useMemo(
    () => wrapQaApiWithUndo(
      createQaApi(client, props.orgId),
      queryClient,
      (entry) => registerRef.current(entry),
      () => activeAreaRef.current,
    ),
    [client, props.orgId, queryClient],
  )
  const jobQuotes = useQaJobQuotes(props.jobId, client)
  const [error, setError] = useState<string | null>(null)
  const capabilities = useQaCapabilities(props.jobId)
  const isMdUp = useIsMdUp()
  const workspace = useQaWorkspace({
    api,
    jobId: props.jobId,
    focusInspectionId: props.focusInspectionId,
    queryClient,
    setError,
    canBuild: capabilities.canBuild,
    openInRun: !isMdUp,
    pickAreaFirst: !isMdUp,
  })
  activeAreaRef.current = workspace.activeAreaId
  const history = useQaHistory(workspace.activeInspectionId)
  registerRef.current = history.register
  const creation = useQaCreation({
    api,
    inspections: workspace.inspections,
    jobContext: props.jobContext,
    jobId: props.jobId,
    quotes: jobQuotes.quotes,
    queryClient,
    selectInspection: workspace.selectInspection,
    openCreatedInspection: workspace.openCreatedInspection,
    setError,
    activeInspectionId: workspace.activeInspectionId,
  })
  const assignee = useQaAssigneeRoster(props.jobId)
  // Its own error state, not the frame's: a refused move ("that area is
  // locked") belongs next to the Move dialog that caused it, and must not
  // replace whatever the tab was already telling the user.
  const tree = useQaTreeMoves({
    api,
    inspectionId: workspace.activeInspectionId,
    areaId: workspace.activeAreaId,
    queryClient,
    registerUndo: history.register,
  })
  const rawSelection = useQaSelection()
  workspace.rememberAssignedClear(rawSelection.clear)
  const areaIds = normalizeList<QaAreaExecution>(workspace.areasQuery.data).map((area) => area.id)
  const { executions } = useQaItpExecutions(areaIds, api, workspace.areaExecutionsReady)
  const follow = (areaId: string | null | undefined, checklistId?: string | null) => {
    if (!areaId || !shouldFocusQaCheck(workspace.activeAreaId, workspace.activeChecklistId, areaId, checklistId)) return
    workspace.focusArea(areaId, checklistId)
  }
  const selection = {
    ...rawSelection,
    toggle: (scope: QaSelectionScope, id: string) => {
      const adding = !rawSelection.isSelected(scope, id)
      if (scope === 'checklists') {
        const children = childrenOfChecklist(executions, id)
        if (children) {
          rawSelection.setHeader(scope, id, adding, {
            itemIds: children.itemIds,
            checklistIds: [],
            coveredByArea: rawSelection.isSelected('areas', children.areaId),
          })
          if (adding) follow(children.areaId, id)
          return
        }
      }
      if (scope === 'areas') {
        const children = childrenOfArea(executions, id)
        if (children) {
          rawSelection.setHeader(scope, id, adding, {
            itemIds: children.itemIds,
            checklistIds: children.checklistIds,
          })
          return
        }
      }
      rawSelection.toggle(scope, id)
      if (!adding) return
      if (scope === 'checklists') follow(areaOfChecklist(executions, id), id)
      if (scope === 'checks') {
        const hit = areaOfCheck(executions, id)
        follow(hit?.areaId, hit?.checklistId)
      }
    },
    toggleCheck: (id: string, checklistId?: string, areaId?: string) => {
      const adding = !rawSelection.isCheckIncluded(id, checklistId, areaId)
      rawSelection.toggleCheck(id, checklistId, areaId)
      if (!adding) return
      const hit = areaOfCheck(executions, id)
      follow(areaId ?? hit?.areaId, checklistId ?? hit?.checklistId)
    },
  }
  const [setupWriteChoice, setSetupWriteChoice] = useState<QaTemplateWriteChoice | null>(null)
  const [templatesNonce, setTemplatesNonce] = useState(0)
  const [templatesInto, setTemplatesInto] = useState<{ checklistId: string; areaId: string; name: string } | null>(null)
  const [containedInNonce, setContainedInNonce] = useState(0)
  const [belongsPoint, setBelongsPoint] = useState<{ x: number; y: number } | null>(null)
  const requestContainedIn = (areaId: string, point?: { x: number; y: number }) => {
    // Reused from: useJobQaTabController.ts (requestOpenTemplates)
    if (point) setBelongsPoint(point)
    if (!selection.isSelected('areas', areaId)) {
      selection.clear()
      selection.toggle('areas', areaId)
    }
    setContainedInNonce((current) => current + 1)
  }
  const requestOpenTemplates = (into?: { checklistId: string; areaId: string; name: string }) => {
    if (!capabilities.canBuild) return
    if (workspace.areaMode !== 'build') workspace.setAreaMode('build')
    setTemplatesInto(into ?? null)
    setTemplatesNonce((current) => current + 1)
  }
  // `client` is exposed for the few surfaces that call an endpoint outside the
  // QA API (the product field searches the quotebuilder pricelist).
  return {
    api,
    canBuild: capabilities.canBuild,
    canCreateQaJob: capabilities.canCreateQaJob,
    client,
    assignee,
    creation,
    error,
    setError,
    history,
    props: {
      ...props,
      quotes: jobQuotes.quotes,
      quotesLoading: jobQuotes.isLoading,
    },
    quotesError: jobQuotes.isError,
    refetchJobQuotes: jobQuotes.refetch,
    requestOpenTemplates,
    templatesInto,
    clearTemplatesInto: () => setTemplatesInto(null),
    requestContainedIn,
    containedInNonce,
    belongsPoint,
    setBelongsPoint,
    setupWriteChoice,
    rememberSetupWrite: (choice: QaTemplateWriteChoice) => setSetupWriteChoice(choice),
    consumeSetupWrite: () => setSetupWriteChoice(null),
    selection,
    templatesNonce,
    tree,
    workspace,
  }
}

export type JobQaTabController = ReturnType<typeof useJobQaTabController>
